import React, { useState, useEffect } from 'react'
import { agnostifyUrl } from '../../../../helpers/utils'
import { createGolContentDataAttributes } from '../../../../helpers/tracking'
import { M0041Props, Alignment } from './types'
import { ComponentHelper, getWrapperProps } from '../../../../helpers/ComponentHelper'
import {
  StyledWrapper,
  StyledHeroImageWrapper,
  StyledHeroImage,
  StyledHeroBlurryImage,
  StyledHeroCopyWrapper,
  StyledHeroHeading,
  StyledHeroVoucherCode,
  StyledButton,
  StyledLegalCopy
} from './M0041.styles'
/**
 * - M004.1 is a banner with Promo Vouchers.
 * - It can be authored with a heading and with or without a subheading, legal copy, voucher code and link.
 * - The text can be left or center aligned.
 * */

const M0041 = ({
  colorOverlay,
  heading,
  subheading,
  textTheme = 'light',
  hasVoucherCode = false,
  voucherCode,
  linkUrl,
  linkTitle,
  linkTarget,
  link = {
    url: '',
    title: '',
    target: ''
  },
  onLinkClick,
  backgroundImageUrl,
  alignment,
  textAlignment = Alignment.CENTER,
  legalCopy,
  type,
  consumingGridType,
  positionInGrid
}: M0041Props): React.ReactElement => {
  const [backgroundStyle, setBackgroundStyle] = useState('')
  useEffect(() => {
    setBackgroundStyle(
      backgroundImageUrl
        ? `url('${ComponentHelper.dynamicImageParams(backgroundImageUrl, ComponentHelper.windowWidth(), type)}')`
        : null
    )
  }, [backgroundImageUrl, ComponentHelper.windowWidth(), type])

  const backgroundStyleStart = `url('${ComponentHelper.dynamicImageParams(backgroundImageUrl)}')`

  const getGOLDataAttributes = (action?: string) =>
    createGolContentDataAttributes(
      consumingGridType,
      `M0041 - ${positionInGrid !== undefined ? `Position${positionInGrid}` : 'NA'}`,
      linkTitle || link?.title,
      linkUrl || link?.url,
      heading,
      action
    )

  const addGolTracking = (event: React.MouseEvent) => {
    const dataAttributes = getGOLDataAttributes()

    Object.keys(dataAttributes).forEach((attribute) =>
      event.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const wrapperClick = (event) => {
    if (onLinkClick) {
      onLinkClick(event, agnostifyUrl(linkUrl || link?.url))
    }
    addGolTracking(event)
  }

  const [buttonText, setButtonText] = useState('Copy')

  const handleCopyToClipboard = (e) => {
    e.preventDefault()
    navigator.clipboard.writeText(voucherCode)
    setButtonText('Copied')

    setTimeout(() => {
      setButtonText('Copy')
    }, 2000)
  }

  return (
    <StyledWrapper
      {...getGOLDataAttributes('Load')}
      data-test='m0041-container'
      as={!linkUrl && !link?.url ? 'div' : undefined}
      target={linkUrl || link?.url ? linkTarget || link?.target : undefined}
      {...getWrapperProps(linkTitle || link?.title, linkUrl || link?.url, (e) => wrapperClick(e))}
    >
      <StyledHeroImageWrapper data-test='m0041-image-container' colorOverlay={colorOverlay || undefined} />
      {backgroundImageUrl && (
        <>
          <StyledHeroImage data-test='m0041-image' backgroundImage={backgroundStyle} />
          <StyledHeroBlurryImage data-test='m0041-blurry-image' backgroundImage={backgroundStyleStart} />
        </>
      )}
      <StyledHeroCopyWrapper
        data-test='m0041-text-container'
        hasLegalCopy={!!legalCopy}
        textAlignment={alignment || textAlignment}
        textTheme={textTheme}
      >
        <StyledHeroHeading data-test='m0041-heading'>{heading}</StyledHeroHeading>
        {subheading && <p data-test='m0041-subheading'>{subheading}</p>}
        {voucherCode && (
          <StyledHeroVoucherCode
            data-test='m0041-voucher'
            textAlignment={alignment || textAlignment}
            colorOverlay={colorOverlay || undefined}
            textTheme={textTheme}
            hasVoucherCode={hasVoucherCode}
          >
            {voucherCode}
            {hasVoucherCode && (
              <StyledButton
                role='button'
                data-test='m0041-copy-button'
                forwardedAs='span'
                onClick={handleCopyToClipboard}
                kind='secondary'
                colorOverlay={colorOverlay || undefined}
                textTheme={textTheme}
              >
                {buttonText}
              </StyledButton>
            )}
          </StyledHeroVoucherCode>
        )}
        {legalCopy && <StyledLegalCopy data-test='m0041-legalCopy'>{legalCopy}</StyledLegalCopy>}
      </StyledHeroCopyWrapper>
    </StyledWrapper>
  )
}
export default M0041
